import React, { useState, useEffect, useCallback, useRef } from 'react';
import Layout from '../components/Layout';
import { alertsApi, notificationsApi } from '../services/api';
import { formatINR } from '../utils/formatters';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Bell, BellRinging, Trash, Plus } from '@phosphor-icons/react';

const CONDITIONS = [
  { key: 'ABOVE', label: 'Price rises above (₹)' },
  { key: 'BELOW', label: 'Price falls below (₹)' },
  { key: 'PCT_UP', label: '% up from now (%)' },
  { key: 'PCT_DOWN', label: '% down from now (%)' },
];

export default function Alerts() {
  const [alerts, setAlerts] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const seenNotifIds = useRef(new Set());  // tracks IDs we've already shown as browser push
  const [symbol, setSymbol] = useState('RELIANCE');
  const [condition, setCondition] = useState('ABOVE');
  const [threshold, setThreshold] = useState(3000);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [a, n] = await Promise.all([alertsApi.list(), notificationsApi.list()]);
      setAlerts(a.data.alerts || []);
      const fresh = n.data.notifications || [];
      // Seed the "already seen" set on first load so we don't fire browser
      // notifications for pre-existing entries.
      if (seenNotifIds.current.size === 0) {
        for (const notif of fresh) seenNotifIds.current.add(notif.id);
      }
      setNotifications(fresh);
    } catch (e) {
      console.error('alerts load failed', e);
    } finally {
      setLoading(false);
    }
  }, []);

  // Browser Notification API — ask permission once
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(async () => {
      try {
        const { data } = await notificationsApi.list();
        const fresh = data.notifications || [];
        for (const n of fresh) {
          if (seenNotifIds.current.has(n.id)) continue;
          seenNotifIds.current.add(n.id);
          if (!n.read && 'Notification' in window && Notification.permission === 'granted') {
            const notif = new Notification('QuantoAura Alert', { body: n.message });
            notif.onclick = () => window.focus();
          }
        }
        setNotifications(fresh);
      } catch (e) {
        console.error('notif poll failed', e);
      }
    }, 30000);
    return () => clearInterval(timer);
  }, [load]);

  const createAlert = useCallback(async () => {
    try {
      await alertsApi.create({
        symbol: symbol.toUpperCase(),
        condition,
        threshold: Number(threshold),
      });
      toast.success(`Alert created for ${symbol}`);
      setThreshold(3000);
      await load();
    } catch (e) {
      console.error('alert create failed', e);
      toast.error(e.response?.data?.detail || 'Failed to create alert');
    }
  }, [symbol, condition, threshold, load]);

  const toggleAlert = useCallback(async (a) => {
    try {
      await alertsApi.toggle(a.id, !a.active);
      await load();
    } catch (e) {
      console.error('alert toggle failed', e);
    }
  }, [load]);

  const removeAlert = useCallback(async (a) => {
    if (!window.confirm(`Delete alert for ${a.symbol}?`)) return;
    try {
      await alertsApi.remove(a.id);
      await load();
    } catch (e) {
      console.error('alert delete failed', e);
    }
  }, [load]);

  const markAllRead = useCallback(async () => {
    try {
      await notificationsApi.markAllRead();
      await load();
    } catch (e) {
      console.error('mark all read failed', e);
    }
  }, [load]);

  if (loading) return <Layout><div className="p-8 text-center text-[#94A3B8]">Loading…</div></Layout>;

  return (
    <Layout>
      <div className="w-full max-w-[1920px] mx-auto px-6 py-6" data-testid="alerts-page">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <BellRinging size={32} weight="duotone" className="text-[#F59E0B]" />
            Price Alerts
          </h1>
          <p className="text-sm text-[#94A3B8] mt-1">
            Set thresholds on any Indian stock. Notifications arrive in-app and as browser push.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-6">
          <CreateForm symbol={symbol} setSymbol={setSymbol} condition={condition} setCondition={setCondition}
            threshold={threshold} setThreshold={setThreshold} onSubmit={createAlert} />
          <div className="space-y-6">
            <AlertsList alerts={alerts} onToggle={toggleAlert} onRemove={removeAlert} />
            <NotificationsList notifications={notifications} onMarkAllRead={markAllRead} />
          </div>
        </div>
      </div>
    </Layout>
  );
}

function CreateForm({ symbol, setSymbol, condition, setCondition, threshold, setThreshold, onSubmit }) {
  const activeCondition = CONDITIONS.find((c) => c.key === condition);
  return (
    <div className="border border-white/10 p-4 h-fit space-y-3" data-testid="alerts-create-form">
      <h3 className="text-sm font-bold text-white flex items-center gap-2">
        <Plus size={16} /> Create Alert
      </h3>
      <div>
        <label className="block text-[10px] uppercase text-[#94A3B8] mb-1.5">Symbol</label>
        <Input value={symbol} onChange={(e) => setSymbol(e.target.value.toUpperCase())}
          className="bg-[#0A0B0E] border-white/20 text-white" data-testid="alert-symbol" />
      </div>
      <div>
        <label className="block text-[10px] uppercase text-[#94A3B8] mb-1.5">Condition</label>
        <select value={condition} onChange={(e) => setCondition(e.target.value)}
          className="w-full bg-[#0A0B0E] border border-white/20 text-white p-2 text-sm"
          data-testid="alert-condition">
          {CONDITIONS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
      </div>
      <div>
        <label className="block text-[10px] uppercase text-[#94A3B8] mb-1.5">{activeCondition.label}</label>
        <Input type="number" step={0.01} value={threshold} onChange={(e) => setThreshold(Number(e.target.value))}
          className="bg-[#0A0B0E] border-white/20 text-white" data-testid="alert-threshold" />
      </div>
      <Button onClick={onSubmit} className="w-full bg-[#F59E0B] hover:bg-orange-500 text-black font-semibold"
        data-testid="alert-submit">
        Create Alert
      </Button>
    </div>
  );
}

function AlertsList({ alerts, onToggle, onRemove }) {
  if (alerts.length === 0) return (
    <div className="border border-dashed border-white/10 p-6 text-center text-[#64748B] text-sm">
      No alerts yet — create one on the left.
    </div>
  );
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.2em] text-[#64748B] mb-2">Your Alerts ({alerts.length})</p>
      <div className="space-y-2" data-testid="alerts-list">
        {alerts.map((a) => (
          <div key={a.id}
            className="flex items-center justify-between border border-white/10 p-3 bg-white/[0.02]"
            data-testid={`alert-row-${a.id}`}>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-mono text-white font-semibold">{a.symbol}</span>
                <span className="text-[10px] uppercase font-mono px-2 py-0.5 border border-white/10 text-[#94A3B8]">
                  {a.condition}
                </span>
                <span className="text-sm text-white">
                  {a.condition.startsWith('PCT') ? `${a.threshold}%` : formatINR(a.threshold)}
                </span>
                {a.triggered_at && (
                  <span className="text-[10px] text-[#F59E0B] font-mono">
                    triggered @ ₹{a.triggered_price}
                  </span>
                )}
              </div>
              <div className="text-[10px] text-[#64748B] mt-0.5">Created {a.created_at?.slice(0, 10)}</div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => onToggle(a)}
                className={`text-[10px] uppercase px-2 py-1 border ${
                  a.active ? 'border-[#10B981] text-[#10B981]' : 'border-white/20 text-[#94A3B8]'
                }`}
                data-testid={`alert-toggle-${a.id}`}
              >
                {a.active ? 'Active' : 'Inactive'}
              </button>
              <button onClick={() => onRemove(a)}
                className="text-[#EF4444] hover:bg-[#EF4444]/10 p-2"
                data-testid={`alert-delete-${a.id}`}>
                <Trash size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function NotificationsList({ notifications, onMarkAllRead }) {
  const unread = notifications.filter((n) => !n.read).length;
  if (notifications.length === 0) return null;
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#64748B]">
          Notifications ({notifications.length}) — {unread} unread
        </p>
        {unread > 0 && (
          <button onClick={onMarkAllRead}
            className="text-[10px] text-[#2563EB] hover:underline"
            data-testid="notif-mark-all-read">
            Mark all read
          </button>
        )}
      </div>
      <div className="space-y-2" data-testid="notifications-list">
        {notifications.map((n) => (
          <div key={n.id}
            className={`p-3 border ${n.read ? 'border-white/10 bg-white/[0.01] text-[#94A3B8]' : 'border-[#F59E0B]/30 bg-[#F59E0B]/5'}`}
            data-testid={`notif-${n.id}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bell size={14} className={n.read ? 'text-[#64748B]' : 'text-[#F59E0B]'} />
                <span className="text-sm text-white">{n.message}</span>
              </div>
              <span className="text-[10px] text-[#64748B]">
                {new Date(n.fired_at).toLocaleString('en-IN', { hour12: false })}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
